import { parseQuestionnaireAccessRequest } from "web_myplann/Api/Nsure/parseQuestionnaireAccessRequest";
import {
	type NsureQuestionnaireAccessRequestDTO,
	ReminderEntityTypeEnum,
	TimelineItemCategoryEnum,
	type TimelineItemResponseDTO,
	TimelineItemSubcategoryEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import { EventSourceEnum } from "web_myplann/NotificationCenter/EventAlert/types";
import { buildContractEventRoute } from "web_myplann/NotificationCenter/Utils/buildContractEventRoute";
import { buildReminderEntityRoute } from "web_myplann/NotificationCenter/Utils/buildReminderEntityRoute";
import { getZzjBridgeUrl } from "web_myplann/NotificationCenter/Utils/getZzjBridgeUrl";
import { parseClientActionData } from "web_myplann/NotificationCenter/Utils/parseClientActionData";
import { parseContractEventData } from "web_myplann/NotificationCenter/Utils/parseContractEventData";
import { parseIdentificationDocumentData } from "web_myplann/NotificationCenter/Utils/parseIdentificationDocumentData";
import { parseReminderData } from "web_myplann/NotificationCenter/Utils/parseReminderData";
import { parseTimelineReminderId } from "web_myplann/NotificationCenter/Utils/parseTimelineReminderId";

export type TimelineNavigation =
	| { kind: "bridge"; url: string }
	| { kind: "route"; path: string }
	| { kind: "questionnaire"; request: NsureQuestionnaireAccessRequestDTO };

const buildPortfolioEventRoute = (item: TimelineItemResponseDTO): string => {
	const eventData = parseContractEventData(item.data);

	return buildContractEventRoute({
		portfolioId: item.entityId,
		eventSource: EventSourceEnum.NotificationCenter,
		eventType: eventData?.eventType,
		eventDate: eventData?.eventDate,
	});
};

const REMINDER_SUBCATEGORY_ENTITY_TYPE: Partial<
	Record<TimelineItemSubcategoryEnum, ReminderEntityTypeEnum>
> = {
	[TimelineItemSubcategoryEnum.ReminderPortfolio]:
		ReminderEntityTypeEnum.Portfolio,
	[TimelineItemSubcategoryEnum.ReminderProperty]:
		ReminderEntityTypeEnum.Property,
	[TimelineItemSubcategoryEnum.ReminderIdentificationDocument]:
		ReminderEntityTypeEnum.IdentificationDocument,
};

const NOTIFICATION_ROUTE_BUILDERS: Partial<
	Record<TimelineItemSubcategoryEnum, (item: TimelineItemResponseDTO) => string>
> = {
	[TimelineItemSubcategoryEnum.ContractEnd]: buildPortfolioEventRoute,
	[TimelineItemSubcategoryEnum.ContractAnniversary]: buildPortfolioEventRoute,
	[TimelineItemSubcategoryEnum.ContractFixationEnd]: buildPortfolioEventRoute,
	[TimelineItemSubcategoryEnum.IdentificationDocumentExpiry]: (item) =>
		Routing.Profile.PaperworkDetail.path(
			parseIdentificationDocumentData(item.data)?.identificationDocumentId ??
				item.entityId,
		),
	[TimelineItemSubcategoryEnum.NotificationTask]: (item) =>
		Routing.TaskDetail.path(item.entityId),
	[TimelineItemSubcategoryEnum.NotificationActivity]: (item) =>
		Routing.ActivityDetail.path(item.entityId),
};

type RouteBuilder = (item: TimelineItemResponseDTO) => string | null;

const ROUTE_BUILDERS: Record<TimelineItemCategoryEnum, RouteBuilder> = {
	[TimelineItemCategoryEnum.Contract]: buildPortfolioEventRoute,
	[TimelineItemCategoryEnum.Activity]: (item) =>
		Routing.ActivityDetail.path(item.entityId),
	[TimelineItemCategoryEnum.Task]: (item) =>
		Routing.TaskDetail.path(item.entityId),
	[TimelineItemCategoryEnum.Reminder]: (item) => {
		const entityType = item.subcategory
			? REMINDER_SUBCATEGORY_ENTITY_TYPE[item.subcategory]
			: undefined;
		return entityType
			? buildReminderEntityRoute(
					entityType,
					item.entityId,
					parseTimelineReminderId(item.data),
				)
			: null;
	},
	[TimelineItemCategoryEnum.ClientAction]: () => null,
	[TimelineItemCategoryEnum.IdentificationDocument]: (item) =>
		Routing.Profile.PaperworkDetail.path(item.entityId),
	[TimelineItemCategoryEnum.Notification]: (item) =>
		item.subcategory
			? (NOTIFICATION_ROUTE_BUILDERS[item.subcategory]?.(item) ?? null)
			: null,
};

const resolveTimelineRoute = (item: TimelineItemResponseDTO): string | null => {
	const buildRoute = ROUTE_BUILDERS[item.category];
	return buildRoute ? buildRoute(item) : null;
};

export const resolveTimelineNavigation = (
	item: TimelineItemResponseDTO,
): TimelineNavigation | null => {
	if (
		item.subcategory ===
		TimelineItemSubcategoryEnum.ClientActionInvestmentQuestionnaire
	) {
		// Stejná validace jako u pushe (`getQuestionnaireAccessRequest`), ale bez
		// Sentry/toastu: tohle běží při renderu (`isNavigable`) a `data` s null
		// hodnotami je legitimní stav už vyplněného dotazníku, ne chyba. Vadná
		// položka se prostě nenabídne k prokliku; v sekci „právě řešíte“, kde je
		// klikací vždy, hlásí chybu až proklik (`useNavigateFromResolvingNowItem`).
		const request = parseQuestionnaireAccessRequest(item.data);
		return request ? { kind: "questionnaire", request } : null;
	}

	if (item.category === TimelineItemCategoryEnum.ClientAction) {
		return null;
	}

	if (item.category === TimelineItemCategoryEnum.Notification) {
		if (item.subcategory === TimelineItemSubcategoryEnum.ReminderTerm) {
			const reminderData = parseReminderData(item.data);
			// BE contract for ReminderTerm data isn't guaranteed — skip navigation
			// rather than guessing the target entity.
			if (!reminderData) {
				return null;
			}
			return {
				kind: "route",
				path: buildReminderEntityRoute(
					reminderData.entityType,
					reminderData.entityId ?? item.entityId,
					reminderData.reminderId,
				),
			};
		}

		// The client-action schema is strict (it requires clientActionRequestId
		// alongside url/token), so payloads that aren't client actions fail to
		// parse and fall through to the generic route below.
		const clientActionData = parseClientActionData(item.data);
		if (clientActionData) {
			const bridgeUrl = getZzjBridgeUrl(clientActionData);
			return bridgeUrl ? { kind: "bridge", url: bridgeUrl } : null;
		}
	}

	const path = resolveTimelineRoute(item);
	return path ? { kind: "route", path } : null;
};
